import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ProductGrid } from '../components/products/ProductGrid';
import { ArrowRight, Sparkles, Mail, Check, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes, newRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?bestseller=true`),
          axios.get(`${API}/products/new-arrivals`),
        ]);
        setCategories(catRes.data);
        setBestsellers(prodRes.data.slice(0, 8));
        setNewArrivals(newRes.data.slice(0, 8));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categoryImages = {
    'Books': 'https://images.pexels.com/photos/9223391/pexels-photo-9223391.jpeg',
    'Crystals': 'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg',
    'Incense': 'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg',
    'Soaps': 'https://images.unsplash.com/photo-1730378616989-499041edc37a',
    'Toys': 'https://images.pexels.com/photos/31061855/pexels-photo-31061855.jpeg',
    'Miscellaneous': 'https://images.pexels.com/photos/32533253/pexels-photo-32533253.jpeg',
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <img
          src="https://images.pexels.com/photos/6309854/pexels-photo-6309854.jpeg"
          alt="Cozy bookstore interior"
          className="hero-image"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="hero-content text-white">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 animate-fade-in">
            Brookzone Bookseller
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl animate-fade-in stagger-1 px-4">
            Your cozy corner for books, crystals, incense, and treasures that nurture the mind, body, and spirit.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in stagger-2 w-full px-4 sm:w-auto">
            <Link to="/products" className="w-full sm:w-auto">
              <Button size="lg" className="btn-accent text-base sm:text-lg px-6 sm:px-8 w-full" data-testid="shop-now-btn">
                Shop Now
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </Link>
            <Link to="/category/books" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 bg-white/10 border-white/30 text-white hover:bg-white/20 w-full">
                Browse Books
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="categories-section">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4">
            Explore Our Collections
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-4">
            From timeless books to healing crystals, find something that speaks to your soul.
          </p>
        </div>

        <div className="category-grid">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="category-card bg-muted animate-pulse" />
            ))
          ) : (
            categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/category/${category.name.toLowerCase()}`}
                className={`category-card group animate-fade-in stagger-${index + 1}`}
                data-testid={`category-card-${category.name.toLowerCase()}`}
              >
                <img
                  src={category.image_url || categoryImages[category.name] || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
                  alt={category.name}
                  className="category-image"
                  loading="lazy"
                />
                <div className="category-overlay">
                  <h3 className="font-serif text-base sm:text-xl md:text-2xl font-bold text-white mb-0.5 sm:mb-1">
                    {category.name}
                  </h3>
                  <p className="text-white/80 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3 hidden sm:block">
                    {category.description}
                  </p>
                  <span className="inline-flex items-center gap-1 sm:gap-2 text-white/90 text-xs sm:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Shop Now <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <section className="bg-emerald-50/50 py-10 sm:py-16" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <Star className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                  <span className="text-emerald-700 font-semibold text-xs sm:text-sm uppercase tracking-wide">Just Added</span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                  New Arrivals
                </h2>
              </div>
              <Link to="/products" className="hidden sm:block">
                <Button variant="outline" data-testid="view-all-new-btn">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <ProductGrid products={newArrivals} loading={loading} />

            <div className="mt-6 sm:mt-8 text-center sm:hidden">
              <Link to="/products">
                <Button variant="outline" className="w-full">
                  View All New Arrivals
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Bestsellers Section */}
      <section className="bg-muted/50 py-10 sm:py-16" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                <span className="text-accent font-semibold text-xs sm:text-sm uppercase tracking-wide">Customer Favorites</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Bestsellers
              </h2>
            </div>
            <Link to="/products" className="hidden sm:block">
              <Button variant="outline" data-testid="view-all-btn">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <ProductGrid products={bestsellers} loading={loading} />

          <div className="mt-6 sm:mt-8 text-center sm:hidden">
            <Link to="/products">
              <Button variant="outline" className="w-full">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Simple Email Subscribe Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <EmailSubscribe />
      </section>
    </div>
  );
};

// Simple Email Subscribe Component
const EmailSubscribe = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      toast.success('Thanks for subscribing!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You\'re already subscribed!');
        setSubscribed(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="bg-primary rounded-lg p-8 sm:p-12 text-center text-primary-foreground">
        <div className="flex justify-center mb-4">
          <div className="bg-green-500/20 p-3 rounded-full">
            <Check className="h-8 w-8 text-green-300" />
          </div>
        </div>
        <h2 className="font-serif text-2xl font-bold mb-2">You're Subscribed!</h2>
        <p className="text-primary-foreground/80">
          We'll let you know about new arrivals and special offers.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-primary rounded-lg p-8 sm:p-12 text-center text-primary-foreground">
      <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-2">
        Stay Updated
      </h2>
      <p className="text-primary-foreground/80 mb-6 max-w-md mx-auto">
        Get notified about new arrivals and exclusive offers.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className="pl-10 bg-white text-foreground h-12"
            data-testid="subscribe-email-input"
          />
        </div>
        <Button 
          type="submit" 
          size="lg"
          disabled={loading || !email}
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8"
          data-testid="subscribe-submit-btn"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            'Subscribe'
          )}
        </Button>
      </form>
      <p className="text-xs text-primary-foreground/60 mt-4">
        No spam, unsubscribe anytime.
      </p>
    </div>
  );
};

export default Home;
